import { applyGroupOrder, hiddenNavEntries } from '@efacture/core/sector';
import type { ModuleKey, Regime, ResolvedProfile } from '@efacture/core/sector';
import { navigationGroups, regimeAllows } from './nav-registry';
import type { NavGroup, NavItem } from './nav-registry';
import type { JurisdictionSurfaces } from '@/api/queries/jurisdiction';

/**
 * Everything the sidebar composition depends on, passed in rather than
 * read from hooks: composition is the part worth testing on its own, and
 * a collapsed group renders none of its items into the DOM, so a test
 * that only reads the rendered menu cannot see what emphasis did.
 */
export interface NavComposeInput {
  /** Defaults to the shipped registry; overridden only by tests. */
  groups?: readonly NavGroup[];
  profile: ResolvedProfile | null;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (...permissions: string[]) => boolean;
  /** Effective plan features, or `null` when billing state is unknown. */
  planFeatures: readonly string[] | null;
  accountingSystem: Regime | null;
  mcfEnDouteCount: number;
  isDesktop: boolean;
  /**
   * What the company's jurisdiction provides, or `null` while unknown: an
   * item whose `surface` is not provided is hidden; unknown hides nothing.
   */
  surfaces?: JurisdictionSurfaces | null;
  /** Nav group keys the role hoists. Empty leaves the registry order. */
  groupOrder?: readonly string[];
  /**
   * The switches the company turned off (`nav.disabled`, MODULE_SWITCHES),
   * or `null` while never loaded: unknown hides nothing.
   */
  companyDisabled?: readonly string[] | null;
  /** The item keys the user hid from their own menu (`nav.hidden`). */
  userHidden?: readonly string[];
}

/**
 * The visible menu: the filtering terms, the company's switch-off, the
 * user's hidden items, then the role's order. The last two only remove,
 * so a user can never show what the company switched off.
 *
 * Ordering is applied LAST, over what the filters already produced, and
 * through `applyGroupOrder`, which is a permutation. A role can therefore
 * move a group it holds but never restore one a filter removed (I-RP1).
 */
export function composeNav(input: NavComposeInput): NavGroup[] {
  const {
    groups = navigationGroups,
    profile,
    hasPermission,
    hasAnyPermission,
    planFeatures,
    accountingSystem,
    mcfEnDouteCount,
    isDesktop,
    surfaces = null,
    groupOrder = [],
    companyDisabled = null,
    userHidden = [],
  } = input;

  // A group key and an item key may be equal (`cabinet`, `funding`): the
  // company layer names whole groups and group/item pairs apart, and the
  // user layer names items only.
  const switchedOff = hiddenNavEntries(companyDisabled ?? []);
  const hidden = new Set(userHidden);
  const dropSwitchedOff = (group: NavGroup): NavGroup[] =>
    switchedOff.groups.has(group.key)
      ? []
      : [{ ...group, items: group.items.filter((item) => !switchedOff.items.has(`${group.key}/${item.key}`)) }];
  const dropHidden = (group: NavGroup): NavGroup => ({
    ...group,
    items: group.items.filter((item) => item.heading === true || !hidden.has(item.key)),
  });

  const planAllows = (feature?: string) =>
    planFeatures === null || !feature || planFeatures.includes(feature);

  // I-SP2 — a profile that is absent, still loading, or unrestricted
  // hides nothing: an unknown sector shows the full menu, so permission
  // remains the only hard boundary.
  const inProfile = (key: ModuleKey) =>
    profile === null || profile.unrestricted || profile.modules.includes(key);

  // Profiles are authored at group granularity: a group listed in
  // `modules` grants every item inside it, even one not individually
  // listed. A `packOnly` item opts out of that grant and demands its own
  // key, so a pack whose items sit in groups every profile already holds
  // still has something to add.
  const itemInProfile = (item: NavItem, group: NavGroup) =>
    item.packOnly === true
      ? inProfile(item.key)
      : inProfile(item.key) || inProfile(group.key);

  const filtered = groups
    .filter((group) => inProfile(group.key))
    .filter(
      (group) => !group.permission || hasPermission(group.permission)
    )
    .filter((group) => planAllows(group.planFeature))
    .filter((group) => regimeAllows(group, accountingSystem))
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          regimeAllows(item, accountingSystem) &&
          // MCF arbitration is gated on a row existing, not on a plan
          // option: nothing to arbitrate means no entry at all.
          !(item.key === 'mcf-signatures-en-doute' && mcfEnDouteCount === 0)
      ),
    }))
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          item.heading === true ||
          (!(item.desktopHidden && isDesktop) &&
            !(item.surface !== undefined && surfaces?.[item.surface] === false) &&
            itemInProfile(item, group) &&
            planAllows(item.planFeature) &&
            (!item.permission || hasPermission(item.permission)) &&
            (!item.anyPermission || hasAnyPermission(...item.anyPermission)))
      ),
    }))
    .flatMap(dropSwitchedOff)
    .map(dropHidden)
    // Sous-titre orphelin (aucun item visible avant le sous-titre suivant)
    .map((group) => ({
      ...group,
      items: group.items.filter((item, index, all) => {
        if (!item.heading) return true;
        for (let i = index + 1; i < all.length; i += 1) {
          if (all[i]!.heading) return false;
          return true;
        }
        return false;
      }),
    }))
    .filter((group) => group.items.some((item) => !item.heading));

  return applyGroupOrder(filtered, groupOrder);
}

/**
 * The composed menu as (group, item) pairs — the shape a test compares
 * across two compositions. Pairing the item with its group is what stops
 * an entry migrating between groups from passing as unchanged.
 */
export function navEntryPairs(groups: readonly NavGroup[]): string[] {
  const pairs: string[] = [];
  for (const group of groups) {
    for (const item of group.items) {
      if (item.heading !== true) pairs.push(`${group.key}/${item.key}`);
    }
  }
  return pairs;
}
